"use client";

import React from "react";

import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Divider,
} from "@nextui-org/react";
import { MoreVertical } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

export interface RealtorContextMenuProps {
  /** Property ID for actions */
  propertyId: string;
  /** Whether menu is disabled */
  disabled?: boolean;
  /** CSS class name for custom styling */
  className?: string;
  /** CSS class name for trigger button */
  triggerClassName?: string;
  /** Menu placement */
  placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
  /** Custom trigger element */
  customTrigger?: React.ReactNode;
  /** Show dangerous actions (delete, etc.) */
  showDangerActions?: boolean;
  /** Action callbacks */
  onEdit?: () => void;
  onArchive?: () => void;
  onPromote?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  onViewStats?: () => void;
}

/**
 * RealtorContextMenu - NextUI Dropdown context menu for realtor property actions
 *
 * UI Specification:
 * - 40×40px trigger button (круг) с тремя точками
 * - Min-width: 224px, Max-width: 280px
 * - Border-radius: 16px, фон: #FFFFFF, бордер: 1px solid #ECECF1
 * - Тень: 0 12px 28px rgba(20, 20, 43, 0.16), 0 2px 6px rgba(20, 20, 43, 0.06)
 * - Высота строки: 40px, padding: 10px 16px
 * - Hover: фон #F7F7F8, Active: фон #F0F1F3
 *
 * Список пунктов (фиксированный порядок):
 * 1. Актуализировать — primary/danger (красный текст), font-weight: 600
 * 2. Дублировать — обычный
 * 3. Отказаться от объекта — обычный
 * 4. Добавить в подборку — обычный
 *
 * @example
 * ```tsx
 * <RealtorContextMenu
 *   propertyId="123"
 *   placement="bottom-end"
 *   onEdit={() => handleActualize('123')}
 *   onDuplicate={() => handleDuplicate('123')}
 *   onArchive={() => handleReject('123')}
 *   onPromote={() => handleAddToSelection('123')}
 * />
 * ```
 */
export const RealtorContextMenu: React.FC<RealtorContextMenuProps> = ({
  propertyId,
  disabled = false,
  className,
  triggerClassName,
  placement = "bottom-end",
  customTrigger,
  showDangerActions = false,
  onEdit,
  onArchive,
  onPromote,
  onDuplicate,
  onDelete,
  onShare,
  onViewStats,
}) => {
  const t = useTranslations("realtor");

  // Default trigger button - using simple HTML button like the working test
  const DefaultTrigger = () => (
    <button
      type="button"
      className={cn(
        // Размер кнопки: 40×40px (круг) по умолчанию
        "w-10 h-10 min-w-10 min-h-10",
        // Фон по умолчанию: #FFFFFF, hover: #F7F7F8
        "bg-white hover:bg-gray-50",
        // Бордер: 1px solid #EFEFF2
        "border border-gray-200",
        // Rounded like the working test
        "rounded-full",
        // Flexbox centering
        "inline-flex items-center justify-center",
        // Тень при hover/focus
        "hover:shadow-md focus-visible:shadow-[0_0_0_3px_rgba(17,86,250,0.06)]",
        // Transitions
        "transition-all duration-200",
        // Active state
        "active:scale-95",
        // Disabled state
        "disabled:opacity-50 disabled:cursor-not-allowed",
        triggerClassName,
      )}
      aria-label={t("contextMenuTriggerLabel")}
      data-testid="realtor-context-menu-trigger"
      disabled={disabled}
      onClick={() => {
        console.log("[RealtorContextMenu] DEBUG: Trigger clicked!", {
          propertyId,
        });
      }}
    >
      <MoreVertical className="w-4 h-4 text-gray-500" />
    </button>
  );

  return (
    <div className={cn("inline-flex", className)}>
      {/* КОПИРУЕМ ТОЧНО ТУ ЖЕ СТРУКТУРУ КАК У РАБОТАЮЩЕГО ТЕСТА */}
      <Dropdown>
        <DropdownTrigger>{customTrigger || <DefaultTrigger />}</DropdownTrigger>
        <DropdownMenu
          aria-label={t("contextMenuMenuLabel")}
          onAction={(key) => {
            console.log("[RealtorContextMenu] DEBUG: Menu action:", {
              key,
              propertyId,
            });
            switch (key) {
              case "actualize":
                onEdit?.();
                break;
              case "duplicate":
                onDuplicate?.();
                break;
              case "reject":
                onArchive?.();
                break;
              case "addToSelection":
                onPromote?.();
                break;
              case "delete":
                onDelete?.();
                break;
              case "share":
                onShare?.();
                break;
              case "viewStats":
                onViewStats?.();
                break;
            }
          }}
          itemClasses={{
            base: "min-h-[48px] px-4 py-3",
            title: "text-[15px] font-medium leading-[1.4]",
          }}
        >
          {/* Актуализировать — красный цвет */}
          <DropdownItem
            key="actualize"
            className="text-red-600 font-semibold"
          >
            {t("contextMenuEditText") || "Актуализировать"}
          </DropdownItem>

          {/* Дублировать */}
          <DropdownItem key="duplicate">
            {t("contextMenuDuplicateText") || "Дублировать"}
          </DropdownItem>

          {/* Отказаться от объекта */}
          <DropdownItem key="reject">
            {t("contextMenuArchiveText") || "Отказаться от объекта"}
          </DropdownItem>

          {/* Добавить в подборку */}
          <DropdownItem key="addToSelection">
            {t("contextMenuPromoteText") || "Добавить в подборку"}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};
